import {Dropdown} from '@/components-v2/molecules'
import {useGetCategoryDropdown} from '@/hooks/promotion/useGetCategoryDropdown'
import {useMemo} from 'react'

/**
 * @typedef {Object} CategoriesDropdownProps
 * @property {Array<{categoryId?: string, categoryName?: string}>} [value] Selected categories.
 * @property {(value: Array<{categoryId: string, categoryName: string}>) => void} [onChange] Callback triggered with the updated selected categories.
 */

/**
 * Multi-select dropdown for product categories, backed by the category list API.
 * @param {CategoriesDropdownProps} props
 */
export const CategoriesDropwdown = ({value = [], onChange = () => {}}) => {
  const {data, isLoading} = useGetCategoryDropdown('categories')

  const options = useMemo(() => {
    if (!data?.data) return []
    return data.data.map(category => ({
      id: category.id,
      displayText: category.en,
    }))
  }, [data])

  const selectedValue = useMemo(
    () =>
      value
        .map(category => {
          const id = category?.categoryId
          if (id == null) return null
          const matchedOption = options.find(option => option.id === id)
          return {id, displayText: matchedOption?.displayText ?? category?.categoryName ?? ''}
        })
        .filter(Boolean),
    [value, options]
  )

  const handleChange = nextSelectedValue => {
    onChange(
      (nextSelectedValue ?? []).map(option => ({
        categoryId: option.id,
        categoryName: option.displayText,
      }))
    )
  }

  return (
    <Dropdown
      childList={options}
      selection="multiple"
      skeleton={isLoading}
      selectedValue={selectedValue}
      setSelectedValue={handleChange}
      placeholder="Categories"
    />
  )
}
